// app/components/privacy-graph-scrolly/elements/node-card.tsx
//
// Uniform 150×56 SVG card. Lucide icon + service name on the top row,
// resource id on the bottom row. Color paints stroke + icon + text together
// so state is read at a glance: default | workload | drift | focus | affected.

import {
  Globe,
  Zap,
  Sparkles,
  Database,
  Archive,
  Layers,
  type LucideIcon,
} from "lucide-react";
import {
  NODE_CARD,
  NODE_META,
  DRIFT_STROKE,
  DRIFT_TEXT,
  FINDING_STROKE,
  type NodeState,
} from "../graph-layout";

export type ServiceIconId =
  | "api-gw"
  | "lambda"
  | "bedrock"
  | "dynamo"
  | "s3"
  | "ddb-snap";

const ICON: Record<ServiceIconId, LucideIcon> = {
  "api-gw":   Globe,
  "lambda":   Zap,
  "bedrock":  Sparkles,
  "dynamo":   Database,
  "s3":       Archive,
  "ddb-snap": Layers,
};

interface StatePalette {
  stroke: string;
  strokeWidth: number;
  strokeDash?: string;
  fill: string;
  iconColor: string;
  nameColor: string;
  metaColor: string;
  opacity: number;
}

const PALETTE: Record<NodeState | "workload", StatePalette> = {
  default: {
    stroke: "#E3E7ED",
    strokeWidth: 1,
    fill: "#FFFFFF",
    iconColor: "#11161D",
    nameColor: "#11161D",
    metaColor: "#4A5361",
    opacity: 1,
  },
  workload: {
    stroke: "rgba(47,107,99,0.45)",
    strokeWidth: 1,
    fill: "rgba(47,107,99,0.03)",
    iconColor: "#11161D",
    nameColor: "#11161D",
    metaColor: "#4A5361",
    opacity: 1,
  },
  // Alarm states use solid-white fills now — translucent tints let the dot
  // grid + zone outline bleed through, which the user flagged as unintentional.
  drift: {
    stroke: DRIFT_STROKE,
    strokeWidth: 1.5,
    fill: "#FFFFFF",
    iconColor: DRIFT_STROKE,
    nameColor: DRIFT_TEXT,
    metaColor: DRIFT_TEXT,
    opacity: 1,
  },
  focus: {
    stroke: FINDING_STROKE,
    strokeWidth: 1.5,
    fill: "#FFFFFF",
    iconColor: FINDING_STROKE,
    nameColor: "#8A5F13",
    metaColor: "#8A5F13",
    opacity: 1,
  },
  affected: {
    stroke: FINDING_STROKE,
    strokeWidth: 1.25,
    strokeDash: "4 4",
    fill: "#FFFFFF",
    iconColor: FINDING_STROKE,
    nameColor: "#8A5F13",
    metaColor: "#8A5F13",
    opacity: 0.6,
  },
};

interface NodeCardProps {
  id: ServiceIconId;
  /** Top-left x in SVG coordinates. */
  x: number;
  /** Top-left y in SVG coordinates. */
  y: number;
  /** Override card width. Defaults to NODE_CARD.width. */
  w?: number;
  /** Visual state. "workload" tints the card sage; alarm states light it up. */
  state?: NodeState | "workload";
  /** Optional dim multiplier (0..1). */
  opacity?: number;
  /** When true, hide the resource id row — used to render an "unmapped"
   *  inventory state in beat 1 before the scan attaches details. */
  hideResourceId?: boolean;
}

export function NodeCard({
  id,
  x,
  y,
  w = NODE_CARD.width,
  state = "default",
  opacity = 1,
  hideResourceId = false,
}: NodeCardProps) {
  const meta = NODE_META[id];
  if (!meta) throw new Error(`NodeCard: unknown node id "${id}"`);

  const p = PALETTE[state];
  const Icon = ICON[id];
  const h = NODE_CARD.height;
  const px = NODE_CARD.paddingX;
  const py = NODE_CARD.paddingY;
  const iconSize = NODE_CARD.iconSize;
  const iconGap = NODE_CARD.iconGap;
  const rowGap = NODE_CARD.rowGap;

  const iconY = py;
  const nameX = px + iconSize + iconGap;
  // Optical-center the 13px text baseline against the icon's vertical midline.
  const nameBaselineY = iconY + iconSize / 2 + 4;
  const resourceBaselineY = iconY + iconSize + rowGap + 8;

  return (
    <g transform={`translate(${x}, ${y})`} opacity={opacity * p.opacity}>
      <g className="graph-node-card">
        {/* Surface */}
        <rect
          width={w}
          height={h}
          rx={NODE_CARD.radius}
          fill={p.fill}
          stroke={p.stroke}
          strokeWidth={p.strokeWidth}
          strokeDasharray={p.strokeDash}
        />
        {/* Lucide icon — nested SVG, sized to our 20px slot. */}
        <g transform={`translate(${px}, ${iconY})`}>
          <Icon
            width={iconSize}
            height={iconSize}
            color={p.iconColor}
            strokeWidth={1.75}
            aria-hidden
          />
        </g>
        {/* Service name (sans, semibold, 13px) */}
        <text
          x={nameX}
          y={nameBaselineY}
          fontSize={13}
          fontWeight={600}
          fill={p.nameColor}
          fontFamily="var(--font-sans, system-ui, sans-serif)"
        >
          {meta.service}
        </text>
        {/* Resource id (mono, 11px) — hidden in the "unmapped" beat 1 state */}
        {!hideResourceId && (
          <text
            x={px}
            y={resourceBaselineY}
            fontSize={11}
            fill={p.metaColor}
            fontFamily="var(--font-mono, ui-monospace, SFMono-Regular, monospace)"
          >
            {meta.resourceId}
          </text>
        )}
      </g>
    </g>
  );
}
